import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactQuill from 'react-quill';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiTrash2, FiEdit2, FiSearch, FiX } from 'react-icons/fi';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import 'react-quill/dist/quill.snow.css';
import { Card, CardHeader, CardContent, CardFooter } from './ui/Card';
import { Button } from './ui/Button';
import { API_URL, secret_key, storedData } from './Constants';
import CryptoJS from 'crypto-js';
import './UserManual.css'; // Custom CSS overrides

export default function UserManual() {
  // Decrypt token and user
  const encryptedToken = localStorage.getItem('token');
  const token = encryptedToken
    ? `Bearer ${CryptoJS.AES.decrypt(encryptedToken, secret_key).toString(CryptoJS.enc.Utf8)}`
    : '';
  const userData = storedData
    ? JSON.parse(CryptoJS.AES.decrypt(storedData, secret_key).toString(CryptoJS.enc.Utf8))
    : {};
  const isAdmin = userData.role_id === 4;

  // State
  const [sections, setSections] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [scrollProgress, setScrollProgress] = useState(0);
  const contentRef = useRef(null);

  // Fetch on mount
  useEffect(() => {
    const fetchSections = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/user_manual/sections`, { headers: { Authorization: token } });
        setSections(data); setFiltered(data);
        if (data.length) setSelected(data[0]);
      } catch (err) {
        console.error('Failed to load sections', err);
        Swal.fire({ icon: 'error', title: 'Load Error', text: 'Unable to fetch sections.' });
      }
    };
    fetchSections();
  }, [token]);

  // Ensure selected exists
  useEffect(() => {
    if (!filtered.length) return;
    if (!selected || !filtered.find(sec => sec.id === selected.id)) {
      setSelected(filtered[0]); setEditMode(false); setShowNew(false);
    }
  }, [filtered, selected]);

  // Filter
  useEffect(() => {
    const term = searchTerm.toLowerCase();
    setFiltered(sections.filter(sec => sec.title.toLowerCase().includes(term)));
  }, [searchTerm, sections]);

  // Scroll handler for progress bar
  const handleScroll = () => {
    if (!contentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    setScrollProgress((scrollTop / (scrollHeight - clientHeight)) * 100);
  };

  // Actions
  const handleSelect = sec => { setSelected(sec); setEditMode(false); setShowNew(false); };

  const handleSave = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await axios.put(`${API_URL}/user_manual/update_section/${selected.id}`, { title: selected.title, content: selected.content }, { headers: { Authorization: token } });
      setSections(prev => prev.map(s => s.id === selected.id ? selected : s));
      setEditMode(false);
      Swal.fire({ icon: 'success', title: 'Saved!', text: 'Section updated successfully.' });
    } catch (err) {
      console.error('Update failed', err);
      Swal.fire({ icon: 'error', title: 'Save Failed', text: 'Could not update the section.' });
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/user_manual/create_section`,
        { title: newTitle, content: newContent },
        { headers: { Authorization: token } }
      );
   // assume data.insertId exists
   const newSection = {
     id: data.insertId,
     title: newTitle,
     content: newContent,
      category: null,
       created_at: new Date().toISOString(),
    };
     setSections(prev => [...prev, newSection]);
     setSelected(newSection);
      Swal.fire({ icon: 'success', title: 'Created!', text: 'New section added.' });
    } catch (err) {
      console.error('Create failed', err);
      Swal.fire({ icon: 'error', title: 'Creation Failed', text: 'Could not create section.' });
    } finally {
      setLoading(false);
      setShowNew(false);
      setNewTitle('');
      setNewContent('');
    }
  };
 

  const handleDelete = async () => {
    if (!selected) return;
    const result = await Swal.fire({ title: 'Confirm Delete', text: 'This will remove the section permanently.', icon: 'warning', showCancelButton: true });
    if (!result.isConfirmed) return;
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/user_manual/delete_section/${selected.id}`, { headers: { Authorization: token } });
      const updated = sections.filter(s => s.id !== selected.id);
      setSections(updated); setFiltered(updated);
      Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Section has been deleted.' });
    } catch (err) {
      console.error('Delete failed', err);
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: 'Could not delete section.' });
    } finally { setLoading(false); }
  };

  return (
    <div className="user-manual-container flex h-full bg-gray-100 dark:bg-gray-900">
      {/* Sidebar */}
      <nav className="sidebar w-64 bg-white dark:bg-gray-800 shadow-xl">
        <div className="header p-6 border-b dark:border-gray-700 flex items-center justify-between">
          <h1 className="text-2xl font-extrabold text-gray-800 dark:text-gray-100">User Manual</h1>
          {isAdmin && <Button variant="ghost" className="icon-btn" onClick={() => { setShowNew(true); setSearchTerm(''); }}><FiPlus /></Button>}
        </div>
        <div className="search px-6 py-4">
          <div className="relative">
            <FiSearch className="absolute top-3 left-3 text-gray-400" />
            <input className="search-input" type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
        </div>
        <ul className="section-list">
          {filtered.map(sec => (
            <motion.li key={sec.id} layout>
              <button className={`section-btn ${selected?.id === sec.id ? 'active' : ''}`} onClick={() => handleSelect(sec)}>
                {sec.title}
              </button>
            </motion.li>
          ))}
        </ul>
      </nav>

      {/* Content */}
      <main className="content flex-1 p-8 overflow-auto">
        <AnimatePresence>
          {showNew && (
            <motion.div className="modal-backdrop" key="new-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div className="modal-card" initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}>
                <CardHeader className="flex justify-between items-center px-6 py-4 border-b dark:border-gray-700">
                  <h2 className="text-lg font-semibold dark:text-white">New Section</h2>
                  <Button variant="ghost" className="icon-btn" onClick={() => setShowNew(false)}><FiX /></Button>
                </CardHeader>
                <CardContent className="px-6 py-4">
                  <input className="form-input mb-4" type="text" placeholder="Title" value={newTitle} onChange={e => setNewTitle(e.target.value)} />
                  <ReactQuill className="quill-editor" theme="snow" value={newContent} onChange={setNewContent} />
                </CardContent>
                <CardFooter className="flex justify-end space-x-4 px-6 py-4 border-t dark:border-gray-700">
                  <Button variant="outline" className="cancel-btn" onClick={() => setShowNew(false)}>Cancel</Button>
                  <Button className="primary-btn" onClick={handleCreate} disabled={loading}>{loading ? 'Creating...' : 'Create'}</Button>
                </CardFooter>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {selected ? (
          <div className="max-w-4xl mx-auto">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${scrollProgress}%` }} />
            </div>
            <Card className="content-card">
              <CardHeader className="flex justify-between items-center px-6 py-4 border-b dark:border-gray-700">
                <h2 className="text-2xl font-bold dark:text-white">{selected.title}</h2>
                {isAdmin && (
                  <div className="flex space-x-2">
                    <Button variant="ghost" className="icon-btn" onClick={() => setEditMode(prev => !prev)}><FiEdit2 /></Button>
                    {editMode && <Button variant="destructive" className="delete-btn" onClick={handleDelete} disabled={loading}><FiTrash2 /></Button>}
                  </div>
                )}
              </CardHeader>
              <CardContent
              onScroll={handleScroll}
              ref={contentRef}
              className="px-6 py-6 max-h-[70vh] overflow-auto transition-all duration-300 ease-in-out"
              >
              {editMode ? (
              <motion.div
              key={`editor-${selected.id}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
              >
              <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-100">Editing: {selected.title}</h2>
              <button
              className="primary-btn text-sm px-4 py-2"
              onClick={() => setEditMode(false)}
              >
              Preview
              </button>
              </div>
              <ReactQuill
              className="quill-editor-large"
              theme="snow"
              value={selected.content}
              onChange={val => setSelected(s => ({ ...s, content: val }))}
              />
              </motion.div>
              ) : (
              <motion.div
              key={`preview-${selected.id}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="prose dark:prose-invert leading-relaxed transition-all duration-300"
              >
              <div dangerouslySetInnerHTML={{ __html: selected.content }} />
              </motion.div>
              )}
              </CardContent>

              {editMode && (
                <CardFooter className="flex justify-end px-6 py-4 border-t dark:border-gray-700">
                  <Button className="primary-btn-lg" onClick={handleSave} disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</Button>
                </CardFooter>
              )}
            </Card>
          </div>
        ) : (
          <div className="text-center text-gray-600 dark:text-gray-400 mt-32">
            <p className="text-lg">No section selected. Please choose one from the sidebar.</p>
          </div>
        )}
      </main>
    </div>
  );
}
